import api from './api';
import { ApiResponse, PageResponse } from '../types/api';
import { 
  ProductRequestDTO, 
  ProductResponseDTO, 
  ProductFilters,
  ProductPriceRequestDTO,
  ProductPriceResponseDTO
} from '../types/product';

const baseUrl = '/v1/produtos';
const pricesUrl = '/v1/precos';

export const productService = {
  // Listar produtos com paginação
  async list(params?: ProductFilters & { page?: number; size?: number; sort?: string }): Promise<ApiResponse<PageResponse<ProductResponseDTO>>> {
    const searchParams = new URLSearchParams();
    
    if (params?.page !== undefined) searchParams.append('page', params.page.toString());
    if (params?.size !== undefined) searchParams.append('size', params.size.toString());
    if (params?.sort) searchParams.append('sort', params.sort);
    
    // Filtros
    if (params?.nome) searchParams.append('nome', params.nome);
    if (params?.codigo) searchParams.append('codigo', params.codigo);
    if (params?.categoriaId) searchParams.append('categoriaId', params.categoriaId);
    if (params?.fornecedorId) searchParams.append('fornecedorId', params.fornecedorId);
    if (params?.embalagemId) searchParams.append('embalagemId', params.embalagemId);
    if (params?.ativo !== undefined) searchParams.append('ativo', params.ativo.toString());
    
    const queryString = searchParams.toString();
    const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;
    
    const response = await api.get<ApiResponse<PageResponse<ProductResponseDTO>>>(url);
    return response.data;
  },

  // Listar produtos ativos via endpoint dedicado (com filtros opcionais)
  async listActive(params?: { page?: number; size?: number; nome?: string; codigo?: string; sort?: string }): Promise<ApiResponse<PageResponse<ProductResponseDTO>>> {
    const searchParams = new URLSearchParams();
    if (params?.page !== undefined) searchParams.append('page', params.page.toString());
    if (params?.size !== undefined) searchParams.append('size', params.size.toString());
    if (params?.nome) searchParams.append('nome', params.nome);
    if (params?.codigo) searchParams.append('codigo', params.codigo);
    if (params?.sort) searchParams.append('sort', params.sort);
    const query = searchParams.toString();
    const url = query ? `${baseUrl}/ativos?${query}` : `${baseUrl}/ativos`;
    const response = await api.get<ApiResponse<PageResponse<ProductResponseDTO>>>(url);
    return response.data;
  },

  // Buscar produto por ID
  async getById(id: string): Promise<ApiResponse<ProductResponseDTO>> {
    const response = await api.get<ApiResponse<ProductResponseDTO>>(`${baseUrl}/${id}`);
    return response.data;
  },

  // Criar novo produto
  async create(data: ProductRequestDTO): Promise<ApiResponse<ProductResponseDTO>> {
    const response = await api.post<ApiResponse<ProductResponseDTO>>(baseUrl, data);
    return response.data;
  },

  // Atualizar produto
  async update(id: string, data: ProductRequestDTO): Promise<ApiResponse<ProductResponseDTO>> {
    const response = await api.put<ApiResponse<ProductResponseDTO>>(`${baseUrl}/${id}`, data);
    return response.data;
  },

  // Excluir produto
  async delete(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`${baseUrl}/${id}`);
    return response.data;
  },

  // Ativar produto
  async activate(id: string): Promise<ApiResponse<ProductResponseDTO>> {
    const response = await api.patch<ApiResponse<ProductResponseDTO>>(`${baseUrl}/${id}/ativar`);
    return response.data;
  },

  // Desativar produto
  async deactivate(id: string): Promise<ApiResponse<ProductResponseDTO>> {
    const response = await api.patch<ApiResponse<ProductResponseDTO>>(`${baseUrl}/${id}/desativar`);
    return response.data;
  },

  // Buscar produto por código
  async getByCodigo(codigo: string): Promise<ApiResponse<ProductResponseDTO>> {
    const searchParams = new URLSearchParams();
    searchParams.append('codigo', codigo);
    
    const response = await api.get<ApiResponse<PageResponse<ProductResponseDTO>>>(`${baseUrl}?${searchParams.toString()}`);
    
    // Extrair o primeiro produto da lista paginada
    if (response.data && response.data.success && response.data.data && response.data.data.content && response.data.data.content.length > 0) {
      return {
        success: true,
        message: 'Produto encontrado',
        data: response.data.data.content[0],
        timestamp: new Date().toISOString()
      };
    }
    
    return {
      success: false,
      message: 'Produto não encontrado',
      data: null as any,
      timestamp: new Date().toISOString()
    };
  },

  // Buscar produto por código de barras (EAN/UPC)
  async getByCodigoBarras(codigoBarras: string): Promise<ApiResponse<ProductResponseDTO>> {
    const response = await api.get(`${baseUrl}/codigo-barras/${encodeURIComponent(codigoBarras)}`);
    // O backend pode retornar ApiResponse<ProductResponseDTO> ou o objeto direto
    if (response.data && typeof response.data === 'object') {
      // Se já vier embrulhado como ApiResponse
      if ('success' in response.data && 'data' in response.data) {
        return response.data as ApiResponse<ProductResponseDTO>;
      }
      // Se vier diretamente o produto
      if ('id' in response.data && 'nome' in response.data) {
        return {
          success: true,
          message: 'Produto encontrado',
          data: response.data as ProductResponseDTO,
          timestamp: new Date().toISOString()
        };
      }
    }
    // Fallback para resposta inesperada
    return {
      success: false,
      message: 'Produto não encontrado',
      data: null as any,
      timestamp: new Date().toISOString()
    };
  },

  // Pesquisar produtos
  async search(query: string, params?: { page?: number; size?: number }): Promise<ApiResponse<PageResponse<ProductResponseDTO>>> {
    const searchParams = new URLSearchParams();
    
    // Usar filtro nome para pesquisa
    searchParams.append('nome', query);
    
    if (params?.page !== undefined) searchParams.append('page', params.page.toString());
    if (params?.size !== undefined) searchParams.append('size', params.size.toString());
    
    const response = await api.get<ApiResponse<PageResponse<ProductResponseDTO>>>(`${baseUrl}?${searchParams.toString()}`);
    return response.data;
  },

  // Pesquisar produtos (rota dedicada): GET /v1/produtos/pesquisar
  async searchByTerm(termo: string, params?: { page?: number; size?: number; sort?: string }): Promise<ApiResponse<PageResponse<ProductResponseDTO>>> {
    const searchParams = new URLSearchParams();
    searchParams.append('termo', termo);
    if (params?.page !== undefined) searchParams.append('page', params.page.toString());
    if (params?.size !== undefined) searchParams.append('size', params.size.toString());
    if (params?.sort) searchParams.append('sort', params.sort);
    const qs = searchParams.toString();
    const url = qs ? `${baseUrl}/pesquisar?${qs}` : `${baseUrl}/pesquisar`;
    const response = await api.get<ApiResponse<PageResponse<ProductResponseDTO>>>(url);
    return response.data;
  },

  // Listar produtos ativos
  async getActive(params?: { page?: number; size?: number }): Promise<ApiResponse<PageResponse<ProductResponseDTO>>> {
    const searchParams = new URLSearchParams();
    
    // Filtrar apenas produtos ativos
    searchParams.append('ativo', 'true');
    
    if (params?.page !== undefined) searchParams.append('page', params.page.toString());
    if (params?.size !== undefined) searchParams.append('size', params.size.toString());
    
    const queryString = searchParams.toString();
    const url = `${baseUrl}?${queryString}`;
    
    const response = await api.get<ApiResponse<PageResponse<ProductResponseDTO>>>(url);
    return response.data;
  },

  // Listar produtos por categoria
  async getByCategory(categoriaId: string, params?: { page?: number; size?: number; sort?: string }): Promise<ApiResponse<PageResponse<ProductResponseDTO>>> {
    const searchParams = new URLSearchParams();
    if (params?.page !== undefined) searchParams.append('page', params.page.toString());
    if (params?.size !== undefined) searchParams.append('size', params.size.toString());
    if (params?.sort) searchParams.append('sort', params.sort);
    const qs = searchParams.toString();
    const url = qs
      ? `${baseUrl}/categoria/${encodeURIComponent(categoriaId)}?${qs}`
      : `${baseUrl}/categoria/${encodeURIComponent(categoriaId)}`;
    const response = await api.get<ApiResponse<PageResponse<ProductResponseDTO>>>(url);
    return response.data;
  },

  // Listar produtos por fornecedor
  async getBySupplier(fornecedorId: string, params?: { page?: number; size?: number }): Promise<ApiResponse<PageResponse<ProductResponseDTO>>> {
    const searchParams = new URLSearchParams();
    
    // Filtrar por fornecedor
    searchParams.append('fornecedorId', fornecedorId);
    
    if (params?.page !== undefined) searchParams.append('page', params.page.toString());
    if (params?.size !== undefined) searchParams.append('size', params.size.toString());
    
    const queryString = searchParams.toString();
    const url = `${baseUrl}?${queryString}`;
    
    const response = await api.get<ApiResponse<PageResponse<ProductResponseDTO>>>(url);
    return response.data;
  },

  // TODO: Implementar validação de código quando endpoint estiver disponível
  // async validateCodigo(codigo: string, produtoId?: string): Promise<ApiResponse<boolean>> {
  //   const searchParams = new URLSearchParams();
  //   searchParams.append('codigo', codigo);
  //   if (produtoId) searchParams.append('produtoId', produtoId);
  //   
  //   const response = await api.get<ApiResponse<boolean>>(`${baseUrl}/validar-codigo?${searchParams.toString()}`);
  //   return response.data;
  // },

  // === OPERAÇÕES DE PREÇOS ===

  // Listar preços de produtos
  async listPrices(params?: { page?: number; size?: number; produtoId?: string }): Promise<ApiResponse<PageResponse<ProductPriceResponseDTO>>> {
    const searchParams = new URLSearchParams();
    
    if (params?.page !== undefined) searchParams.append('page', params.page.toString());
    if (params?.size !== undefined) searchParams.append('size', params.size.toString());
    if (params?.produtoId) searchParams.append('produtoId', params.produtoId);
    
    const queryString = searchParams.toString();
    const url = queryString ? `${pricesUrl}?${queryString}` : pricesUrl;
    
    const response = await api.get<ApiResponse<PageResponse<ProductPriceResponseDTO>>>(url);
    return response.data;
  },

  // Buscar preço por ID
  async getPriceById(id: string): Promise<ApiResponse<ProductPriceResponseDTO>> {
    const response = await api.get<ApiResponse<ProductPriceResponseDTO>>(`${pricesUrl}/${id}`);
    return response.data;
  },

  // Criar novo preço
  async createPrice(data: ProductPriceRequestDTO): Promise<ApiResponse<ProductPriceResponseDTO>> {
    const response = await api.post<ApiResponse<ProductPriceResponseDTO>>(pricesUrl, data);
    return response.data;
  },

  // Atualizar preço
  async updatePrice(id: string, data: ProductPriceRequestDTO): Promise<ApiResponse<ProductPriceResponseDTO>> {
    const response = await api.put<ApiResponse<ProductPriceResponseDTO>>(`${pricesUrl}/${id}`, data);
    return response.data;
  },

  // Ativar preço
  async activatePrice(id: string): Promise<ApiResponse<ProductPriceResponseDTO>> {
    const response = await api.post<ApiResponse<ProductPriceResponseDTO>>(`${pricesUrl}/${id}/ativar`);
    return response.data;
  },

  // Desativar preço
  async deactivatePrice(id: string): Promise<ApiResponse<ProductPriceResponseDTO>> {
    const response = await api.post<ApiResponse<ProductPriceResponseDTO>>(`${pricesUrl}/${id}/desativar`);
    return response.data;
  },

  // Excluir preço
  async deletePrice(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`${pricesUrl}/${id}`);
    return response.data;
  },

  // Obter preço atual de um produto
  async getCurrentPrice(produtoId: string): Promise<ApiResponse<ProductPriceResponseDTO>> {
    const response = await api.get<ApiResponse<ProductPriceResponseDTO>>(`${pricesUrl}/produto/${produtoId}/atual`);
    return response.data;
  },

  // Listar histórico de preços por produto
  async getPriceHistory(produtoId: string, params?: { page?: number; size?: number }): Promise<ApiResponse<PageResponse<ProductPriceResponseDTO>>> {
    const searchParams = new URLSearchParams();
    if (params?.page !== undefined) searchParams.append('page', params.page.toString());
    if (params?.size !== undefined) searchParams.append('size', params.size.toString());
    const qs = searchParams.toString();
    const url = qs
      ? `${pricesUrl}/produto/${encodeURIComponent(produtoId)}/historico?${qs}`
      : `${pricesUrl}/produto/${encodeURIComponent(produtoId)}/historico`;
    const response = await api.get<ApiResponse<any>>(url);
    const original = response.data;
    const raw = original && 'data' in original ? original.data : original;
    // Normalizar para PageResponse
    const page: PageResponse<ProductPriceResponseDTO> =
      Array.isArray(raw)
        ? {
            content: raw as ProductPriceResponseDTO[],
            totalElements: (raw as ProductPriceResponseDTO[]).length,
            totalPages: 1,
            size: (raw as ProductPriceResponseDTO[]).length,
            number: 0,
            first: true,
            last: true,
          }
        : (raw as PageResponse<ProductPriceResponseDTO>);
    return { ...(original || {}), data: page } as ApiResponse<PageResponse<ProductPriceResponseDTO>>;
  }
};

export default productService;
